// Shop team roles (SPEC section 3), shared by the app and the Worker. The
// server decides; the app uses these only to show the right controls.

export const SHOP_ROLES = ['owner', 'manager', 'orders'] as const;
export type ShopRole = (typeof SHOP_ROLES)[number];
export const INVITABLE_ROLES = ['manager', 'orders'] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export type ShopPermission =
  | 'view_team' | 'manage_team' | 'leave_shop'
  | 'edit_location' | 'edit_business_info' | 'edit_hours' | 'edit_profile'
  | 'edit_listings' | 'post_as_shop'
  | 'manage_orders' | 'view_dashboard'
  | 'manage_payouts' | 'change_handle' | 'block_people';

const PERMISSIONS: Record<ShopRole, ReadonlySet<ShopPermission>> = {
  owner: new Set([
    'view_team', 'manage_team', 'edit_location', 'edit_business_info', 'edit_hours', 'edit_profile',
    'edit_listings', 'post_as_shop', 'manage_orders', 'view_dashboard', 'manage_payouts', 'change_handle', 'block_people',
  ]),
  manager: new Set([
    'view_team', 'leave_shop', 'edit_location', 'edit_business_info', 'edit_hours', 'edit_profile',
    'edit_listings', 'post_as_shop', 'manage_orders', 'view_dashboard', 'block_people',
  ]),
  orders: new Set(['view_team', 'leave_shop', 'manage_orders']),
};

export const ROLE_LABELS: Record<ShopRole, string> = { owner: 'Owner', manager: 'Manager', orders: 'Orders' };

export const ROLE_DESCRIPTIONS: Record<ShopRole, string> = {
  owner: 'Everything, including payouts, the team and the shop handle.',
  manager: 'Listings, orders, opening hours, business info, location and posts as the shop.',
  orders: 'Accept, reject, pack and deliver orders.',
};

export function isShopRole(value: unknown): value is ShopRole {
  return value === 'owner' || value === 'manager' || value === 'orders';
}

export function isInvitableRole(value: unknown): value is InvitableRole {
  return value === 'manager' || value === 'orders';
}

export function shopRoleCan(role: unknown, permission: ShopPermission): boolean {
  return isShopRole(role) && PERMISSIONS[role].has(permission);
}

export const SHOP_INVITE_TTL_DAYS = 7;
export const MAX_PENDING_SHOP_INVITES = 20;
export const MAX_SHOPS_PER_ACCOUNT = 5;
export const SHOP_NAME_MAX_LENGTH = 60;

export function maskEmail(email: unknown): string {
  const [local = '', domain = ''] = String(email ?? '').trim().toLowerCase().split('@');
  if (!local || !domain) return '';
  return `${local.slice(0, Math.min(2, local.length))}${'*'.repeat(Math.max(3, local.length - 2))}@${domain}`;
}

export function normalizeEmail(value: unknown): string | null {
  const email = String(value ?? '').trim().toLowerCase();
  return /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/.test(email) && email.length <= 320 ? email : null;
}

export function normalizeShopName(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, SHOP_NAME_MAX_LENGTH);
}
